import React, { useState } from "react";
import { Formik, Field, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import axios from "axios";
import { handleError } from "../../utils";
import http from "../../core/service/http";
import { useNavigate } from "react-router-dom";
import { useFirstViewHook } from "./useFirstViewHook";

const FirstView = ({ handleNext, setData, setSponsorName }) => {
  const {
    isLoading,
    setLoading,
    validationSchema,
    initialValues,
    showPassword,
    showConfirmPassword,
    togglePasswordVisibility,
    toggleConfirmPasswordVisibility,
    handleNavigate,
  } = useFirstViewHook();

  const handleSubmit = async (values, setSubmitting, setFieldError) => {
    try {
      setSubmitting(true);
      const response = await http.post("/v1/auth/validate-register", {
        sponsorId: values.member_id,
        token: values.token,
        email: values.email,
      });
      const data = response.data.data;
      if (data.isEmailValid && data.isTokenValid) {
        handleNext();
        setSponsorName(data.sponsorName);
        setLoading(false);
        return;
      }
      if (!data.isTokenValid) {
        setFieldError("token", "The Token is not valid or already in use.");
      }
      if (!data.isEmailValid) {
        setFieldError("email", "The email is already in use.");
      }
      setSubmitting(false);
    } catch (error) {
      console.error("Login failed", error);
      if (error.response) {
        handleError(error.response.data.message);
      } else {
        handleError("Network error. Please try again later.");
      }
      setSubmitting(false);
    }
  };

  return (
    <div className="container h-100 mb-5 mt-2">
      <div className="row">
        <div className="col-md-12">
          <legend className="text-left header text-success mb-3">
            <h4>GENERAL INFORMATION</h4>
          </legend>
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={(values, { setSubmitting, setFieldError }) => {
              // After form submission, handle the next step
              setData((prevData) => ({
                ...values, // Spread the updated form values
                address: prevData.address, // Preserve address from previous state
                city: prevData.city, // Preserve city from previous state
                zip: prevData.zip, // Preserve zip from previous state
              }));
              handleSubmit(values, setSubmitting, setFieldError);
            }}
            onReset={(values) => {
              // Reset logic
              setData((prevData) => ({
                ...prevData, // Preserve the previous state for address, city, and zip
              }));
            }}
          >
            {({
              handleChange,
              errors,
              touched,
              isSubmitting,
              handleReset,
              resetForm,
            }) => (
              <Form>
                {/* Sponsor ID */}
                <div className="row mb-3">
                  <div className="col-md-4">
                    <div className="form-group">
                      <label htmlFor="member_id" className="required mb-2">
                        Enter Member Id:{" "}
                        <span className="required-icon"> *</span>
                      </label>
                      <div className="input-group">
                        <div className="input-group-prepend">
                          <span className="input-group-text p-spacing">
                            <i className="fa fa-key bigicon"></i>
                          </span>
                        </div>
                        <Field
                          id="member_id"
                          name="member_id"
                          type="text"
                          placeholder="Member Id"
                          className={`form-control ${
                            errors.member_id && touched.member_id
                              ? "field-error"
                              : ""
                          }`}
                          onChange={handleChange}
                        />
                      </div>
                      <ErrorMessage
                        name="member_id"
                        component="div"
                        className="text-danger"
                      />
                    </div>
                  </div>

                  {/* Token Number */}
                  <div className="col-md-4">
                    <div className="form-group">
                      <label htmlFor="token" className="required mb-2">
                        Token Number: <span className="required-icon"> *</span>
                      </label>
                      <div className="input-group">
                        <div className="input-group-prepend">
                          <span className="input-group-text p-spacing">
                            <i className="fa fa-key bigicon"></i>
                          </span>
                        </div>
                        <Field
                          id="token"
                          name="token"
                          type="text"
                          placeholder="Enter Token Number"
                          className={`form-control ${
                            errors.member_id && touched.member_id
                              ? "field-error"
                              : ""
                          }`}
                          onChange={handleChange}
                        />
                      </div>
                      <ErrorMessage
                        name="token"
                        component="div"
                        className="text-danger"
                      />
                    </div>
                  </div>
                </div>

                {/* First Name */}
                <div className="row mb-3">
                  <div className="col-md-4">
                    <div className="form-group">
                      <label htmlFor="firstName" className="required mb-2">
                        First Name: <span className="required-icon"> *</span>
                      </label>
                      <div className="input-group">
                        <div className="input-group-prepend">
                          <span className="input-group-text p-spacing">
                            <i className="fa fa-user bigicon"></i>
                          </span>
                        </div>
                        <Field
                          id="firstName"
                          name="firstName"
                          type="text"
                          placeholder="First Name"
                          className={`form-control ${
                            errors.firstName && touched.firstName
                              ? "field-error"
                              : ""
                          }`}
                          onChange={handleChange}
                        />
                      </div>
                      <ErrorMessage
                        name="firstName"
                        component="div"
                        className="text-danger"
                      />
                    </div>
                  </div>

                  {/* Last Name */}
                  <div className="col-md-4">
                    <div className="form-group">
                      <label htmlFor="lastName" className="required mb-2">
                        Last Name: <span className="required-icon"> *</span>
                      </label>
                      <div className="input-group">
                        <div className="input-group-prepend">
                          <span className="input-group-text p-spacing">
                            <i className="fa fa-user bigicon"></i>
                          </span>
                        </div>
                        <Field
                          id="lastName"
                          name="lastName"
                          type="text"
                          placeholder="Last Name"
                          className={`form-control ${
                            errors.lastName && touched.lastName
                              ? "field-error"
                              : ""
                          }`}
                          onChange={(e) => handleChange(e)}
                        />
                      </div>
                      <ErrorMessage
                        name="lastName"
                        component="div"
                        className="text-danger"
                      />
                    </div>
                  </div>
                </div>

                {/* Password */}
                <div className="row mb-3">
                  <div className="col-md-4">
                    <div className="form-group position-relative">
                      <label htmlFor="password" className="required mb-2">
                        Password: <span className="required-icon"> *</span>
                      </label>
                      <div className="input-group">
                        <div className="input-group-prepend">
                          <span className="input-group-text p-spacing">
                            <i className="fa fa-lock bigicon"></i>
                          </span>
                        </div>
                        <Field
                          id="password"
                          name="password"
                          type={showPassword ? "text" : "password"}
                          placeholder="Password"
                          className={`form-control ${
                            errors.password && touched.password
                              ? "field-error"
                              : ""
                          }`}
                          onChange={(e) => handleChange(e)}
                        />
                      </div>
                      <button
                        type="button"
                        onClick={togglePasswordVisibility}
                        className="btn-eye"
                      >
                        <i
                          className={`fa ${
                            showPassword ? "fa-eye-slash" : "fa-eye"
                          }`}
                        ></i>
                      </button>
                      <ErrorMessage
                        name="password"
                        component="div"
                        className="text-danger"
                      />
                    </div>
                  </div>

                  {/* Confirm Password */}
                  <div className="col-md-4">
                    <div className="form-group position-relative">
                      <label
                        htmlFor="confirmPassword"
                        className="required mb-2"
                      >
                        Confirm Password:{" "}
                        <span className="required-icon"> *</span>
                      </label>
                      <div className="input-group">
                        <div className="input-group-prepend">
                          <span className="input-group-text p-spacing">
                            <i className="fa fa-lock bigicon"></i>
                          </span>
                        </div>
                        <Field
                          id="confirmPassword"
                          name="confirmPassword"
                          type={showConfirmPassword ? "text" : "password"}
                          placeholder="Confirm Password"
                          className={`form-control ${
                            errors.confirmPassword && touched.confirmPassword
                              ? "field-error"
                              : ""
                          }`}
                          onChange={(e) => handleChange(e)}
                        />
                      </div>
                      <button
                        type="button"
                        onClick={toggleConfirmPasswordVisibility}
                        className="btn-eye"
                      >
                        <i
                          className={`fa ${
                            showConfirmPassword ? "fa-eye-slash" : "fa-eye"
                          }`}
                        ></i>
                      </button>
                      <ErrorMessage
                        name="confirmPassword"
                        component="div"
                        className="text-danger"
                      />
                    </div>
                  </div>
                </div>

                {/* Email */}
                <div className="row mb-3">
                  <div className="col-md-4">
                    <div className="form-group">
                      <label htmlFor="email" className="required mb-2">
                        Email: <span className="required-icon"> *</span>
                      </label>
                      <div className="input-group">
                        <div className="input-group-prepend">
                          <span className="input-group-text p-spacing">
                            <i className="fa fa-envelope bigicon"></i>
                          </span>
                        </div>
                        <Field
                          id="email"
                          name="email"
                          type="email"
                          placeholder="Email Address"
                          className={`form-control ${
                            errors.email && touched.email ? "field-error" : ""
                          }`}
                          onChange={(e) => handleChange(e)}
                        />
                      </div>
                      <ErrorMessage
                        name="email"
                        component="div"
                        className="text-danger"
                      />
                    </div>
                  </div>

                  {/* Confirm Email */}
                  <div className="col-md-4">
                    <div className="form-group">
                      <label htmlFor="confirmEmail" className="required mb-2">
                        Confirm Email: <span className="required-icon"> *</span>
                      </label>
                      <div className="input-group">
                        <div className="input-group-prepend">
                          <span className="input-group-text p-spacing">
                            <i className="fa fa-envelope bigicon"></i>
                          </span>
                        </div>
                        <Field
                          id="confirmEmail"
                          name="confirmEmail"
                          type="email"
                          placeholder="Confirm Email Address"
                          className={`form-control ${
                            errors.confirmEmail && touched.confirmEmail
                              ? "field-error"
                              : ""
                          }`}
                          onChange={(e) => handleChange(e)}
                        />
                      </div>
                      <ErrorMessage
                        name="confirmEmail"
                        component="div"
                        className="text-danger"
                      />
                    </div>
                  </div>
                </div>

                {/* Phone Number */}
                <div className="row mb-3">
                  <div className="col-md-4">
                    <div className="form-group">
                      <label htmlFor="phone" className="required mb-2">
                        Contact Number:{" "}
                        <span className="required-icon"> *</span>
                      </label>
                      <div className="input-group">
                        <div className="input-group-prepend">
                          <span className="input-group-text p-spacing">
                            <i className="fa fa-phone-square bigicon"></i>
                          </span>
                        </div>
                        <Field
                          id="phone"
                          name="phone"
                          type="number"
                          placeholder="Phone/Cell"
                          className={`form-control ${
                            errors.phone && touched.phone ? "field-error" : ""
                          }`}
                          onChange={(e) => handleChange(e)}
                        />
                      </div>
                      <ErrorMessage
                        name="phone"
                        component="div"
                        className="text-danger"
                      />
                    </div>
                  </div>
                </div>

                {/* Submit Button */}
                <div className="row my-4">
                  <div className="col-md-7">
                    <button
                      disabled={isSubmitting}
                      type="submit"
                      className="btn btn-success py-2 px-4 btn-lg me-2"
                    >
                      {isSubmitting && (
                        <i className="fas fa-spinner fa-spin"></i>
                      )}
                      {!isSubmitting && "Move to Next Step"}
                    </button>
                    <button
                      type="reset"
                      className="btn btn-warning py-2 px-4 btn-lg me-2 mt-1"
                    >
                      Reset
                    </button>
                    <button
                      className="btn btn-danger py-2 px-4 btn-lg mt-1"
                      onClick={handleNavigate}
                      type="button"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </div>
  );
};

export default FirstView;
